import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { matchApi } from '../../services/api';

type MatchStatus = 'upcoming' | 'live' | 'completed';

interface Match {
  id: number;
  tournament: string;
  team1: string;
  team2: string;
  date: string;
  venue: string;
  status: MatchStatus;
  result?: string;
}

interface MatchState {
  matches: Match[];
  loading: boolean;
  error: string | null;
}

const initialState: MatchState = {
  matches: [],
  loading: false,
  error: null,
};

export const fetchMatches = createAsyncThunk(
  'matches/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await matchApi.getAll();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch matches');
    }
  }
);

export const createMatch = createAsyncThunk(
  'matches/create',
  async (matchData: Omit<Match, 'id'>, { rejectWithValue }) => {
    try {
      const response = await matchApi.create(matchData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create match');
    }
  }
);

export const updateMatch = createAsyncThunk(
  'matches/update',
  async ({ id, data }: { id: number; data: Partial<Match> }, { rejectWithValue }) => {
    try {
      const response = await matchApi.update(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update match');
    }
  }
);

export const deleteMatch = createAsyncThunk(
  'matches/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await matchApi.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete match');
    }
  }
);

const matchSlice = createSlice({
  name: 'matches',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch matches
      .addCase(fetchMatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMatches.fulfilled, (state, action) => {
        state.loading = false;
        state.matches = action.payload;
      })
      .addCase(fetchMatches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create match
      .addCase(createMatch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMatch.fulfilled, (state, action) => {
        state.loading = false;
        state.matches.push(action.payload);
      })
      .addCase(createMatch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update match
      .addCase(updateMatch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMatch.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.matches.findIndex((match) => match.id === action.payload.id);
        if (index !== -1) {
          state.matches[index] = action.payload;
        }
      })
      .addCase(updateMatch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete match
      .addCase(deleteMatch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMatch.fulfilled, (state, action) => {
        state.loading = false;
        state.matches = state.matches.filter((match) => match.id !== action.payload);
      })
      .addCase(deleteMatch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = matchSlice.actions;
export default matchSlice.reducer; 